import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");
const dateOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeOf = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const localDate = () => dateOf(new Date());
const localTime = () => timeOf(new Date());

const HEARTBEAT_MS = 40000;   // ping every 40s while the tab is open & visible
const GRACE_MS = 90000;       // brief refresh / away window that must NOT punch out
const CLOSED_KEY = "vns_att_closed_at"; // per-tab marker; cleared when the tab truly closes

// Keeps attendance time accurate:
//  • Time only accrues while the desk tab is open AND visible (idle pause).
//  • A quick refresh (<90s) auto-resumes the session instead of punching out.
//  • Closing the tab/app punches the operator out.
export function useAttendanceHeartbeat() {
  const { user } = useAuth();
  const empId = user?.employee_id;

  useEffect(() => {
    if (!empId) return;

    const beat = () => {
      if (document.visibilityState !== "visible") return;
      api.post("/attendance/heartbeat", { date: localDate(), time: localTime() }).catch(() => {});
    };

    // Refresh grace: if this tab closed within the grace window, resume seamlessly.
    try {
      const ts = Number(sessionStorage.getItem(CLOSED_KEY) || 0);
      if (ts && Date.now() - ts <= GRACE_MS) {
        api.post("/attendance/resume", { date: localDate(), time: localTime() }).catch(() => {});
      }
      sessionStorage.removeItem(CLOSED_KEY);
    } catch { /* ignore */ }

    beat();
    const interval = setInterval(beat, HEARTBEAT_MS);

    // Idle pause: when the operator switches away for longer than the grace window,
    // cut the session at the moment they left and start fresh when they return —
    // so away time is not counted. Brief switches (<90s) keep counting.
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const idle = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (idle > GRACE_MS) {
        const wentIdle = new Date(Date.now() - idle);
        api
          .post("/attendance/close", { date: dateOf(wentIdle), time: timeOf(wentIdle) })
          .then(() => api.post("/attendance/resume", { date: localDate(), time: localTime() }))
          .then((res) => {
            if (res?.data?.status === "resumed") {
              toast.info("Clock paused — you stepped away", {
                description: "Time didn't count while you were away. Your clock has resumed now.",
                duration: 6000,
              });
            }
          })
          .catch(() => {})
          .finally(beat);
      } else {
        beat();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Immediate punch-out on tab/app close. sendBeacon survives unload.
    const onPageHide = () => {
      try { sessionStorage.setItem(CLOSED_KEY, String(Date.now())); } catch { /* ignore */ }
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend || !navigator.sendBeacon) return;
      const body = new Blob(
        [JSON.stringify({ date: localDate(), time: localTime() })],
        { type: "application/json" }
      );
      navigator.sendBeacon(`${backend}/api/attendance/close`, body);
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [empId]);
}
